import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { routes } from '../../routes/AppRoutes';
import { strings } from '../../resources/strings';
import { OrderType } from '../../enums/OrderType';
import { MarketplaceTypeEnum } from '../../enums/MarketplaceTypeEnum';
import { SessionHelper } from '../../helpers/SessionHelper';
import { MainActions } from '../../actions/MainActions';
import { useAuth } from '../../contexts/AuthContext';
import ServicesContext from '../../contexts/ServicesContext';
import { OrderImportForListing } from '../../models/OrderImportForListing';
import { GetOrderImportsInput } from '../../services/Marketplaces/GetOrderImports/GetOrderImportsInput';
import { ModalInfoType } from '../../common-types/ModalInfoType';
import { Header } from '../../common-components/Layout/Header/Header';
import { IBreadcrumbItem } from '../../common-components/Breadcrumb/Breadcrumb';
import { SectionsPage } from '../../common-components/Layout/SectionsPage/SectionsPage';
import { ActionsBox } from '../../common-components/Layout/ActionsBox/ActionsBox';
import { FlexColumnListHeader } from '../../common-components/Layout/FlexColumnList/FlexColumnListHeader';
import { FlexColumnListRow } from '../../common-components/Layout/FlexColumnList/FlexColumnListRow';
import { FlexColumnListFooter } from '../../common-components/Layout/FlexColumnList/FlexColumnListFooter';
import { IFlexColumnListItem } from '../../common-components/Layout/FlexColumnList/IFlexColumnListItem';
import { IActionsBoxItem } from '../../common-components/Layout/ActionsBox/IActionsBoxItem';
import { If } from '../../common-components/If/If';
import { ModalAmaroNewOrderImport } from '../../components/ModalAmaroNewOrderImport/ModalAmaroNewOrderImport';
import { ModalAmaroOrderImportDetails } from '../../components/ModalAmaroOrderImportDetails/ModalAmaroOrderImportDetails';

const ActionsKey = {
  Filter: 'filter',
  Update: 'update',
  New: 'new'
};

const actions: IActionsBoxItem[] = [
  {
    key: ActionsKey.Update,
    text: 'Atualizar'
  },
  {
    key: ActionsKey.New,
    text: 'Novo'
  }
];

const columns: IFlexColumnListItem[] = [
  {
    field: 'id',
    title: 'Nº',
    width: '70px'
  },
  {
    field: 'createdAt',
    title: 'Data',
    width: '100px',
    type: 'date'
  },
  {
    field: 'filePath',
    title: 'Arquivo',
    width: 'calc(100% - 537px)',
    widthRow: 'calc(100% - 535px)',
    type: 'link'
  },
  {
    field: 'numberOfRecords',
    title: 'Registros',
    width: '100px',
    align: 'center'
  },
  {
    field: 'numberOfRecordsWithSucess',
    title: 'Sucesso',
    width: '100px',
    align: 'center'
  },
  {
    field: 'numberOfRecordsWithError',
    title: 'Erros',
    width: '100px',
    align: 'center'
  },
  {
    field: 'genericalOptionsRow',
    title: 'Opções',
    width: '65px',
    align: 'center',
    type: 'options'
  },
  {
    field: '_',
    title: '',
    width: '2px'
  }
];

const GenericalOptionsRow = {
  Details: 'details'
};

const ListAmaroOrderImports: React.FC = () => {

  const dispatch = useDispatch();
  const mainActions = React.useMemo(() => new MainActions(dispatch), [dispatch]);

  const navigate = useNavigate();
  const { session, signOut } = useAuth();

  const {
    getOrderImportsService,
    toastService
  } = React.useContext(ServicesContext);

  const [currentPage, setCurrentPage] = React.useState<number>(1);
  const [currentPageSize, setCurrentPageSize] = React.useState<number>(0);
  const [amountOfPages, setAmountOfPages] = React.useState<number>(0);
  const [totalAmount, setTotalAmount] = React.useState<number>(0);
  const [pageSize, setPageSize] = React.useState<number>(25);
  const [filtering, setFiltering] = React.useState<boolean>(true);
  const [orderImports, setOrderImports] = React.useState<OrderImportForListing[] | undefined>(undefined);
  const [modalNewOrderImportVisible, setModalNewOrderImportVisible] = React.useState<boolean>(false);
  const [modalOrderImportDetailsInfo, setModalOrderImportDetailsInfo] = React.useState<ModalInfoType<number>>({visible: false});

  const breadcrumbItems: IBreadcrumbItem[] = React.useMemo(() => {
    const memo: IBreadcrumbItem[] = [
      {
        text: strings.Home,
        route: routes.Home
      },
      {
        text: 'Amaro',
        route: routes.ManageAmaroIntegrations
      }
    ];
    return memo;
  }, []);

  useEffect(() => {
    if (!session || SessionHelper.isExpired(session)) {
      signOut(() => {
        navigate(routes.Login);
      });
      return;
    }

    clearFilter(pageSize, currentPage);
  }, [session, navigate]);

  return (
    <>
      <SectionsPage
        loading={filtering}
        header={{
          padding: '0px 63px',
          backgroundColor: '#fff',
          items: [
            <Header
              key={'header'}
              title={'Listagem de Importações de Pedidos Amaro'}
              breadcrumbItems={breadcrumbItems}
            />
          ]
        }}
        body={{
          padding: '0px 63px',
          backgroundColor: 'transparent',
          header: [
            <ActionsBox
              key={'actions-box'}
              defaultItemWidth='80px'
              actions={actions}
              click={actionBoxClick}
              loading={filtering}
            />,
            <FlexColumnListHeader
              name='order_imports_amaro'
              columns={columns}
              loading={filtering}
            />
          ]
        }}
        footer={{
          padding: '10px 63px',
          backgroundColor: '#fff',
          items: [
            <FlexColumnListFooter
              key={'order_imports_amaro_footer'}
              currentPage={currentPage}
              currentPageSize={currentPageSize}
              amountOfPages={amountOfPages}
              totalAmount={totalAmount}
              pageSize={pageSize}
              changePageSize={changePageSize}
              changePage={changePage}
              loading={filtering}
            />
          ]
        }}
      >
        <FlexColumnListRow
          name='order_imports_amaro'
          columns={columns}
          values={orderImports}
          loading={filtering}
          optionGroups={[
            {
              key: 'genericalOptionsRow',
              options: [
                { key: GenericalOptionsRow.Details, text: 'Detalhar' }
              ],
              click: genericalOptionsRowHandle
            }
          ]}
        />
      </SectionsPage>

      <If condition={modalNewOrderImportVisible}>
        <ModalAmaroNewOrderImport
          close={() => {
            setModalNewOrderImportVisible(false);
            updateList(pageSize, currentPage);
          }}
        />
      </If>

      <If condition={modalOrderImportDetailsInfo.visible}>
        <ModalAmaroOrderImportDetails
          orderImportId={modalOrderImportDetailsInfo.key}
          close={() => {
            setModalOrderImportDetailsInfo({...modalOrderImportDetailsInfo, visible: false});
            updateList(pageSize, currentPage);
          }}
        />
      </If>
    </>
  );

  function changePage(page: number) {
    setCurrentPage(page);
    updateList(pageSize, page);
  }

  function changePageSize(size: number) {
    setPageSize(size);
    setCurrentPage(1);
    updateList(size, 1);
  }

  function updateList(size: number, page: number) {
    if (SessionHelper.isExpired(session)) {
      signOut(() => {
        navigate(routes.Login);
      });
      return;
    }
    setCurrentPage(page);
    filter({}, size, page);
  }

  function clearFilter(size: number, page: number) {
    setCurrentPage(page);
    filter({}, size, page);
  }

  function filter(values: any, size: number, page: number) {
    mainActions.setNotWait(true);
    setFiltering(true);
    const filterInput = new GetOrderImportsInput();
    filterInput.pageSize = size > 0 ? size : 1;
    filterInput.currentPage = page;
    filterInput.shouldPaginate = size > 0;
    filterInput.orderType = OrderType.Descending;
    filterInput.marketplaceType = MarketplaceTypeEnum.Amaro;
    getOrderImportsService.handle(filterInput)
      .then(result => {
        setOrderImports(result?.value?.list || []);
        setCurrentPage(result?.value?.currentPage || 0);
        setCurrentPageSize(result?.value?.currentPageSize || 0);
        setAmountOfPages(result?.value?.amountOfPages || 0);
        setTotalAmount(result?.value?.totalAmount || 0);
      })
      .catch(() => {
        toastService.warning(strings.OpsSomethingWrongGettingTheData);
      })
      .finally(() => {
        setTimeout(() => mainActions.setNotWait(false), 1000);
        setFiltering(false);
      });
  }

  function actionBoxClick(key: string) {
    if (key === ActionsKey.Update) {
      updateList(pageSize, currentPage);
      return;
    }
    if (key === ActionsKey.New) {
      setModalNewOrderImportVisible(true);
      return;
    }
  }

  function genericalOptionsRowHandle(key: string, row: OrderImportForListing) {
    if (key === GenericalOptionsRow.Details) {
      setModalOrderImportDetailsInfo({...modalOrderImportDetailsInfo, visible: true, key: row.id});
      return;
    }
  }
}

export default ListAmaroOrderImports;